/**
 * The countdown of the one global action toast (MYDAY_LEAD_COMMS_PLAN.md Appendix C.9, FE-52): pure state, so a spec
 * drives it with plain numbers, plus the clock contract `ActionToastService` schedules it with. The timer pauses while
 * the toast is hovered or focused and resumes with the time that was left (S-13, PR14).
 */

/** The time and one timer. The browser's by default; a spec passes a fake or runs under jasmine's mock clock. */
export interface ActionToastClock {
    /** Epoch milliseconds. */
    now(): number;
    /** Calls `callback` once after `delayMs`; returns a handle for `clearTimer`. */
    setTimer(callback: () => void, delayMs: number): unknown;
    clearTimer(timer: unknown): void;
}

/** `Date.now()` and `setTimeout`, read at call time (so jasmine's mock clock replaces them in specs). */
export const BROWSER_ACTION_TOAST_CLOCK: ActionToastClock = {
    now: () => Date.now(),
    setTimer: (callback: () => void, delayMs: number) => setTimeout(callback, delayMs),
    clearTimer: (timer: unknown) => clearTimeout(timer as ReturnType<typeof setTimeout>),
};

/** A toast's countdown: the time it still has, and since when it has been running (null while it is held). */
export interface ToastCountdown {
    readonly remainingMs: number;
    readonly runningSinceMs: number | null;
}

/** A countdown of `durationMs`, running from `nowMs`. A negative or non-numeric duration is no time at all. */
export function startCountdown(durationMs: number, nowMs: number): ToastCountdown {
    return { remainingMs: isFinite(durationMs) && durationMs > 0 ? durationMs : 0, runningSinceMs: nowMs };
}

/** What is left at `nowMs`: never negative, and a clock that steps back never adds time. */
export function countdownRemainingMs(countdown: ToastCountdown, nowMs: number): number {
    if (countdown.runningSinceMs === null) {
        return countdown.remainingMs;
    }
    return Math.max(0, countdown.remainingMs - Math.max(0, nowMs - countdown.runningSinceMs));
}

/** Holds the countdown at what is left now. Holding a held countdown changes nothing. */
export function pauseCountdown(countdown: ToastCountdown, nowMs: number): ToastCountdown {
    if (countdown.runningSinceMs === null) {
        return countdown;
    }
    return { remainingMs: countdownRemainingMs(countdown, nowMs), runningSinceMs: null };
}

/** Runs a held countdown on from `nowMs` with what it had left. Resuming a running countdown changes nothing. */
export function resumeCountdown(countdown: ToastCountdown, nowMs: number): ToastCountdown {
    if (countdown.runningSinceMs !== null) {
        return countdown;
    }
    return { remainingMs: countdown.remainingMs, runningSinceMs: nowMs };
}
